// Shared shapes for every otto-app route.
import { z } from "zod";

/**
 * Who may call a route:
 *   public          anyone (Turnstile and limits guard the writes)
 *   consult_cookie  the HttpOnly consult cookie set at check-in (only its hash is stored)
 *   admin           a signed-in admin (the sign-in method is Wes's decision D2)
 *   brain           otto-live through the service binding, with the BRAIN_AUTH_HEADER
 */
export type RouteAuth = "public" | "consult_cookie" | "admin" | "brain";

export interface RouteContract {
  method: "GET" | "POST";
  /** Hono style path, e.g. "/api/consult/:id/status". */
  path: string;
  auth: RouteAuth;
  params?: z.ZodType;
  query?: z.ZodType;
  body?: z.ZodType;
  /** The 2xx JSON body. Errors use ApiErrorBody. */
  response: z.ZodType;
}

/** Identity helper that keeps the literal types of a contract. */
export function route<const R extends RouteContract>(contract: R): R {
  return contract;
}

/** Every non-2xx JSON body from otto-app. */
export const ApiErrorBody = z.object({
  ok: z.literal(false),
  error: z.object({
    code: z.string().min(1).max(64),
    /** Plain words, safe to show. No dashes, no vendor names, no secrets. */
    message: z.string().max(500),
    retry_after_seconds: z.number().int().min(0).optional(),
    /** For ROUTE_TO_LEGACY: where the check-in should go instead. */
    redirect_url: z.url().optional(),
    /** Validation problems, one line each (config saves, bad input). */
    issues: z.array(z.string().max(500)).max(100).optional(),
  }),
});
export type ApiErrorBody = z.infer<typeof ApiErrorBody>;

/**
 * Every error code otto-app answers with (ApiErrorBody.error.code), so the
 * web and admin screens can handle each one. CHECKIN_ERROR_CODES (public.ts)
 * are the check-in's subset.
 */
export const API_ERROR_CODES = [
  // check-in (see CHECKIN_ERROR_CODES)
  "invalid_input",
  "turnstile_failed",
  "rate_limited",
  "paused",
  "between_calls",
  "legacy",
  "booked_up",
  "at_capacity",
  "mode_unavailable",
  "language_unavailable",
  "internal",
  // everything else
  "forbidden", // wrong Origin, or a test consult without the key
  "not_found", // no such route or record, or a consult cookie that does not match
  "not_ended", // a rating before the consult ended
  "too_large", // a request body over the route's limit
  "unauthorized", // admin: not signed in
  "admin_not_configured", // admin: no sign-in method configured (the admin is closed)
  "invalid_config", // Otto Studio: a version or the bundle it would make does not validate
  "nothing_to_read_back", // brain: a read-back asked for before any owner words
  "already_sent", // admin: resend of a handoff that was already accepted
  "consult_ended", // live token: the consult is over
  "not_live", // admin: end a call that is not live
  "cannot_repackage", // admin: repackage a consult whose package job has not failed
] as const;
export type ApiErrorCode = (typeof API_ERROR_CODES)[number];

export function apiError(code: string, message: string, extra: Partial<ApiErrorBody["error"]> = {}): ApiErrorBody {
  return { ok: false, error: { code, message, ...extra } };
}

export const Ok = z.literal(true);

export const ConsultIdParams = z.object({ id: z.uuid() });

/** Letters, digits, underscores: channel ids, fact keys, stage ids. */
export const Key = z.string().regex(/^[a-z][a-z0-9_]{0,63}$/);

/**
 * Text typed by a prospect: control characters removed, trimmed, capped
 * (design 5.9: check-in fields are stripped and length capped, then treated
 * as quoted data, never as instructions).
 */
export function typedText(max: number) {
  return z
    .string()
    .max(max * 2)
    .transform((s) => s.replace(/[\u0000-\u001f\u007f]/g, " ").replace(/\s+/g, " ").trim())
    .pipe(z.string().min(1).max(max));
}

export const IsoTime = z.iso.datetime({ offset: true });
